<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="new-template-message-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form
        ref="form"
        class="new-template-message-form"
        @submit.prevent="onSubmit"
      >
        <div class="new-template-message-form-body">
          <v-toolbar flat height="76">
            <v-toolbar-title class="new-template-message-toolbar-title"
              >{{
                editedMessage.message ? 'Edit' : 'New'
              }}
              message</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-container class="new-template-message-card-container">
            <dialog-input
              label="Message"
              name="message"
              :textarea="true"
              :textareaExpandable="true"
              :value="editedMessage.message || ''"
              @input="onInput"
              :readonly="loadingModal"
            />
          </v-container>
          <v-container class="new-template-message-card-container pt-0">
            <time-picker
              @input="onInput"
              :initial-time="editedMessage.time || newMessageData.time"
              :is-submit="messageScheduled"
              :readonly="loadingModal"
            />
          </v-container>
          <v-container class="new-template-message-card-container pt-0">
            <v-row>
              <v-col sm="12">
                <thumbnail-picker
                  label="Thumbnail"
                  :current-file="currentFile"
                  :current-mobile-static-media="currentMobileStaticMedia"
                  @thumbnail-picked="setThumbnail"
                  :readonly="loadingModal"
                  :allowed-file-types="[17, 18, 19, 20, 21, 22, 23, 52]"
                  one-source-only
                  enable-mobile-static-media
                />
              </v-col>
            </v-row>
          </v-container>
          <v-container class="new-template-message-card-container py-0">
            <dialog-checkbox
              label="Acknowledgement required?"
              name="acknowledge"
              :value="newMessageData.acknowledge"
              :option="{ label: 'Yes', value: newMessageData.acknowledge }"
              @input="onInput"
              :readonly="loadingModal"
            />
          </v-container>
        </div>
        <v-container
          class="new-template-message-card-footer d-flex justify-end"
        >
          <v-btn
            :disabled="!formIsValidCheck || loadingModal"
            rounded
            color="primary"
            type="submit"
            elevation="0"
            :loading="loadingModal"
          >
            {{ editedMessage ? 'Save message' : 'Add message' }}
          </v-btn>
        </v-container>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import DialogInput from '@/components/forms/DialogInput.vue'
import DialogCheckbox from '@/components/forms/DialogCheckbox.vue'
// import DialogTimePicker from '@/components/forms/DialogTimePicker.vue'
import TimePicker from '@/components/forms/TimePicker.vue'
import ThumbnailPicker from '@/components/inputs/ThumbnailPicker.vue'
import StaticMediaMixin from '@/mixins/staticMedia'

const emptyForm = {}

export default {
  name: 'TemplateMessageDialog',
  mixins: [StaticMediaMixin],
  components: {
    DialogInput,
    DialogCheckbox,
    // DialogTimePicker,
    TimePicker,
    ThumbnailPicker,
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    resource: {
      type: [Boolean, Object],
      default: false,
    },
    messageResource: {
      type: [Boolean, Object, null],
      default: false,
    },
    day: {
      type: [Boolean, Number],
      default: false,
    },
    editedMessage: {
      type: [Boolean, Object],
      default: false,
    },
    getResource: {
      type: Function,
      default: () => null,
    },
  },
  data() {
    return {
      dialog: false,
      rules: {
        text: [v => !!v || 'Value cannot by empty'], // @todo
      },
      newMessageData: {
        message: '',
        time: '06:00',
        acknowledge: false,
      },
      resourceName: null,
      formIsValidCheck: false,
      messageScheduled: false,
      thumbnailUrl: null,
      mediaUrl: null,
      thumbnailValue: null,
      mediaFileTypeId: null,
      loadingModal: false,
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true
      }
    },
    editedMessage: function(val) {
      if (this.editedMessage) {
        this.newMessageData = { ...this.editedMessage }
        if (this.editedMessage.resourceId) {
          this.$emit('reload-resource', this.editedMessage.resourceId)
        }
        this.formIsValid()
      }
    },
    messageResource: function(val) {
      this.mediaFileTypeId = val?.fileTypeId
      this.mediaUrl = val?.fileUrl || null
      this.thumbnailUrl = val?.thumbnailUrl || val?.previewUrl || null
    },
  },
  computed: {
    currentFile() {
      if (!this.thumbnailUrl && !this.mediaUrl) {
        return null
      }
      return {
        fileTypeId: this.mediaFileTypeId,
        thumbnailUrl: this.thumbnailUrl,
        mediaUrl: this.mediaUrl || null,
      }
    },
    currentMobileStaticMedia() {
      const staticMediaId = this.editedMessage?.staticMediaId
      if (!staticMediaId || !this.staticMedia) return null

      const selectedStaticMedia = this.staticMedia.find(
        el => el.id === staticMediaId,
      )
      if (!selectedStaticMedia) {
        return null
      }

      return {
        mediaUrl: selectedStaticMedia.mediaUrl,
      }
    },
  },
  methods: {
    setLoadingModal(val) {
      this.loadingModal = !!val
    },
    setThumbnail(thumbnail = null) {
      if (thumbnail === null) {
        // remove
        this.thumbnailValue = { remove: true }
        this.editedMessage.staticMediaId = null
      } else {
        const { type, file } = thumbnail

        if (type === 'media-library') {
          this.thumbnailValue = { mlSourceResourceId: file.id }
        } else if (type === 'file-picker') {
          this.thumbnailUrl = null
          this.mediaUrl = null
          this.thumbnailValue = file
        } else if (type === 'mobile-static-media') {
          this.thumbnailUrl = null
          this.mediaUrl = null
          this.thumbnailValue = { staticMediaId: file.id }
        }
      }
    },

    async openBigImage() {
      if (this.messageResource.id) {
        this.$emit('show-bigger-image', {
          url: null,
          file: null,
          name: null,
          mlResourceUrl: null,
        }) // Show dialog while getting new s3 file url
        await this.getResource({ id: this.messageResource.id })
      }
      let type = 1
      switch (this.messageResource.fileTypeId) {
        case 18:
          type = 1
          break
        case 19:
          type = 3
          break
        default:
          type = 1
          break
      }
      const title =
        this.messageResource.title.replace(/[/\\?%*:|"<>]/g, '-') +
        '.' +
        this.messageResource.name
          .split('.')
          .pop()
          .trim()
          .toLowerCase()
      this.$emit('show-bigger-image', {
        url: this.messageResource.fileUrl,
        name: title || this.resourceName,
        file: this.messageResource.previewUrl,
        mlResourceUrl: this.messageResource.id,
        type: type,
      })
    },
    formIsValid() {
      if (this.newMessageData.time && this.newMessageData.message.trim() != '')
        this.formIsValidCheck = true
      else this.formIsValidCheck = false
    },
    closeDialog() {
      this.loadingModal = false
      this.dialog = false
      this.$emit('close-message-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    onInput({ name, value }) {
      this.newMessageData[name] = value
      this.formIsValid()
    },
    onSubmit() {
      this.messageScheduled = true
      if (this.day !== false) this.newMessageData.day = this.day
      this.setLoadingModal(true)
      if (this.editedMessage)
        this.$emit('on-edit', {
          ...this.newMessageData,
          thumbnailValue:
            this.thumbnailValue?.mlSourceResourceId ||
            this.thumbnailValue?.staticMediaId ||
            this.thumbnailValue?.remove
              ? { ...this.thumbnailValue }
              : this.thumbnailValue,
        })
      else
        this.$emit('on-submit', {
          ...this.newMessageData,
          thumbnailValue:
            this.thumbnailValue?.mlSourceResourceId ||
            this.thumbnailValue?.staticMediaId ||
            this.thumbnailValue?.remove
              ? { ...this.thumbnailValue }
              : this.thumbnailValue,
        })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.new-template-message {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    // looks little hacky
    > .v-card {
      width: 100%;
      height: 100%;
    }
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-file-button {
    margin-right: 10px;
    .v-icon {
      &:first-child {
        padding-right: 10px;
      }
      &:last-child {
        padding-left: 10px;
      }
    }
  }
  &-file-details {
    display: inline-block;
    img {
      width: 40px;
      height: 40px;
      display: inline-block;
      vertical-align: middle;
      object-fit: cover;
      margin-right: 10px;
      cursor: pointer;
    }
    .v-progress-circular {
      margin-right: 10px;
    }
    .v-icon {
      margin-left: 5px;
      padding: 4px;
    }
    span {
      cursor: default;
    }
  }
  &-form {
    background-color: $bh-blue--4;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: auto;

    &-body {
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }
}
</style>
